import { Check, X } from 'lucide-react';
import { useState } from 'react';
import { Banner, BigButton, Label, Screen, TextArea } from '../components/ui';
import { saveVehicleCheck } from '../lib/actions';
import { VEHICLE_CHECK_ITEMS } from '../lib/labels';
import { navigate } from '../lib/router';
import { useActiveTrips } from '../lib/selectors';

/**
 * Проверка преди тръгване. Девет точки, всяка с два бутона — това е максимумът,
 * който човек прави честно всяка сутрин. По-дълъг списък се цъка наред, без да
 * се гледа, и тогава не струва нищо.
 */
export function VehicleCheckScreen() {
  const trips = useActiveTrips();
  const trip = trips[0] ?? null;
  const [state, setState] = useState<Record<string, boolean>>({});
  const [odometer, setOdometer] = useState('');
  const [problem, setProblem] = useState('');
  const [busy, setBusy] = useState(false);

  const answered = VEHICLE_CHECK_ITEMS.filter((item) => item.key in state).length;
  const problems = VEHICLE_CHECK_ITEMS.filter((item) => state[item.key] === false);
  const complete = answered === VEHICLE_CHECK_ITEMS.length;

  async function submit() {
    setBusy(true);
    try {
      await saveVehicleCheck({
        tripId: trip?.id ?? null,
        vehicleId: trip?.vehicle?.id ?? null,
        odometerKm: odometer ? Number(odometer.replace(/\D/g, '')) : null,
        items: VEHICLE_CHECK_ITEMS.map((item) => ({ key: item.key, ok: state[item.key] === true })),
        problem: problem.trim().length > 0 ? problem.trim() : null,
      });
      navigate('/', true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Проверка на буса" back>
      <div className="space-y-3">
        {trip?.vehicle ? (
          <p className="text-xl font-semibold">
            {trip.vehicle.plate}
            {trip.vehicle.model ? ` · ${trip.vehicle.model}` : ''}
          </p>
        ) : null}

        <div>
          <Label>Километри по километража</Label>
          <input
            value={odometer}
            onChange={(event) => setOdometer(event.target.value.replace(/\D/g, '').slice(0, 7))}
            inputMode="numeric"
            placeholder="напр. 214300"
            className="mt-1 w-full rounded-xl border-2 border-line bg-paper px-4 py-4 text-2xl"
          />
        </div>

        <ul className="space-y-2">
          {VEHICLE_CHECK_ITEMS.map((item) => (
            <li
              key={item.key}
              className="flex items-center gap-3 rounded-2xl border-2 border-line px-3 py-2"
            >
              <span className="flex-1 text-xl font-semibold">{item.label}</span>
              <button
                type="button"
                aria-label={`${item.label}: наред`}
                onClick={() => setState((value) => ({ ...value, [item.key]: true }))}
                className={`flex h-16 w-16 items-center justify-center rounded-xl border-2 ${
                  state[item.key] === true ? 'border-go bg-go text-white' : 'border-line'
                }`}
              >
                <Check size={30} strokeWidth={3} />
              </button>
              <button
                type="button"
                aria-label={`${item.label}: има проблем`}
                onClick={() => setState((value) => ({ ...value, [item.key]: false }))}
                className={`flex h-16 w-16 items-center justify-center rounded-xl border-2 ${
                  state[item.key] === false ? 'border-stop bg-stop text-white' : 'border-line'
                }`}
              >
                <X size={30} strokeWidth={3} />
              </button>
            </li>
          ))}
        </ul>

        {problems.length > 0 ? (
          <>
            <Banner tone="stop">
              Има забележки: {problems.map((item) => item.label).join(', ')}. Опишете какво не е
              наред — диспечерът го вижда веднага.
            </Banner>
            <TextArea
              label="Какво не е наред"
              value={problem}
              onChange={setProblem}
              placeholder="Например: предна дясна гума с 1,5 бара по-малко"
            />
          </>
        ) : null}

        <BigButton tone="go" disabled={!complete || busy} onClick={() => void submit()}>
          {complete ? 'Готово' : `Остават ${VEHICLE_CHECK_ITEMS.length - answered}`}
        </BigButton>
      </div>
    </Screen>
  );
}
